/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `BusinessEventDetails` for the Caintra Connect frontend.
 * @module pages/BusinessEventDetails
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/BusinessEventDetails.module.css";

import { AppContext } from "../context/AppContext";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";

import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/BusinessEvents/ChevronLeftIcon.svg";

import { use, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { getUser } from "../logic/user";
import {
  initializeEventCreation,
  getParticipatingCompaniesPage,
} from "../logic/businessEvents";
import { getParticipatingCompany } from "../logic/participatingCompany";
import {
  cancelAppointment,
  updateAppointment,
} from "../logic/appointment/index.js";
import dayjs from "dayjs";
import BusinessEventRequirementsDisplayModal from "../modals/BusinessEventRequirementsDisplay/index.jsx";
import AppointmentPaymentModal from "../modals/AppointmentPaymentModal/index.jsx";
import ViewBusinessEventRegistrationModal from "../modals/ViewBusinessEventRegistrationModal/index.jsx";
import PageSelector from "../components/PageSelector/index.jsx";

const PARTICIPANT_ROWS_PER_PAGE = 4;
const PARTICIPANT_CARD_MIN_WIDTH = 240;
const PARTICIPANT_CARD_GAP = 12;
const PARTICIPANT_MAX_COLUMNS = 4;

const BusinessEventDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const { changePermissions } = use(AppContext);

  const [eventData, setEventData] = useState(null);
  const [participatingCompanies, setParticipatingCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [participantsPage, setParticipantsPage] = useState(1);
  const [participantsTotalCount, setParticipantsTotalCount] = useState(0);
  const [participantColumns, setParticipantColumns] = useState(
    PARTICIPANT_MAX_COLUMNS,
  );
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [viewRegistrationModalOpen, setViewRegistrationModalOpen] =
    useState(false);
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [autoSelectRequirementId, setAutoSelectRequirementId] = useState(null);
  const participantsListRef = useRef(null);
  const participantPageCursorsRef = useRef({ 1: null });
  const participantsFetchIdRef = useRef(0);

  // Device detection for responsive search bar
  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1440;
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;

  const getDevice = () => {
    if (isSmallMobile) {
      return "smallMobile";
    } else if (isMediumMobile) {
      return "mediumMobile";
    } else if (isLaptop) {
      return "laptop";
    } else if (isTablet) {
      return "tablet";
    }
    return "desktop";
  };

  const getSearchBarWidth = (device) => {
    if (device === "laptop") {
      return "90%";
    } else if (device === "tablet") {
      return "73%";
    } else if (device === "mediumMobile") {
      return "90%";
    } else if (device === "smallMobile") {
      return "75%";
    }
    return "90%";
  };

  useEffect(() => {
    let isCurrent = true;

    const initializeData = async () => {
      try {
        const userId = localStorage.getItem("userId");
        const userData = await getUser(userId, navigate);
        if (isCurrent && userData) {
          setCurrentUser(userData);
        }
        await initializeEventCreation({
          eventId,
          userId,
          changePermissions,
          navigate,
          dayjs,
          setFormData: setEventData,
          setParticipatingCompanies,
          setIsLoading,
          isFromAdmin: false,
          skipParticipatingCompanies: true,
        });
      } catch (error) {
        logger.error("Error during initial data fetch:", {
          err: sanitizeError(error),
        });
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    setIsLoading(true);
    initializeData();
    return () => {
      isCurrent = false;
    };
  }, [eventId, changePermissions, navigate]);

  useEffect(() => {
    const list = participantsListRef.current;
    if (!list || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateColumns = () => {
      const width = list.clientWidth;
      if (!width) {
        return;
      }
      const fittingColumns = Math.floor(
        (width + PARTICIPANT_CARD_GAP) /
          (PARTICIPANT_CARD_MIN_WIDTH + PARTICIPANT_CARD_GAP),
      );
      setParticipantColumns(
        Math.min(PARTICIPANT_MAX_COLUMNS, Math.max(1, fittingColumns)),
      );
    };

    updateColumns();
    const observer = new ResizeObserver(updateColumns);
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    participantPageCursorsRef.current = { 1: null };
    setParticipantsPage(1);
  }, [participantColumns, debouncedSearchQuery, eventId]);

  const loadParticipatingCompaniesPage = useCallback(
    async ({ page, limit, search }) => {
      const fetchId = ++participantsFetchIdRef.current;
      setParticipantsLoading(true);
      const lastKey =
        page === 1 ? null : (participantPageCursorsRef.current[page] ?? null);
      const data = await getParticipatingCompaniesPage({
        eventId,
        navigate,
        limit,
        lastKey,
        search,
        includeAttendance: true,
      });
      if (fetchId !== participantsFetchIdRef.current) {
        return data;
      }
      if (data.items.length === 0 && page > 1) {
        setParticipantsPage(page - 1);
        setParticipantsLoading(false);
        return data;
      }
      setParticipatingCompanies(data.items);
      if (typeof data.totalCount === "number") {
        setParticipantsTotalCount(data.totalCount);
      }
      if (data.lastEvaluatedKey) {
        participantPageCursorsRef.current[page + 1] = data.lastEvaluatedKey;
      } else {
        delete participantPageCursorsRef.current[page + 1];
      }
      setParticipantsLoading(false);
      return data;
    },
    [eventId, navigate],
  );

  useEffect(() => {
    if (!eventId) {
      return;
    }
    const limit = participantColumns * PARTICIPANT_ROWS_PER_PAGE;
    if (
      participantsPage > 1 &&
      participantPageCursorsRef.current[participantsPage] == null
    ) {
      setParticipantsPage(1);
      return;
    }
    loadParticipatingCompaniesPage({
      page: participantsPage,
      limit,
      search: debouncedSearchQuery,
    });
  }, [
    eventId,
    participantsPage,
    participantColumns,
    debouncedSearchQuery,
    loadParticipatingCompaniesPage,
  ]);

  useEffect(() => {
    const openFromNavigation = async () => {
      if (
        !location.state?.autoOpenRequirements ||
        !location.state?.participatingCompanyId
      ) {
        return;
      }
      const company = await getParticipatingCompany(
        eventId,
        location.state.participatingCompanyId,
        navigate,
      );
      if (!company) {
        return;
      }
      setSelectedParticipant({
        ...company,
        idEvento: eventId,
        idEmpresaCompradora: location.state.participatingCompanyId,
      });
      setDisplayModalOpen(true);
      if (location.state.businessEventRequirementId) {
        setAutoSelectRequirementId(location.state.businessEventRequirementId);
      }
      navigate(location.pathname, { replace: true, state: {} });
    };

    openFromNavigation();
  }, [location.state, eventId, navigate, location.pathname]);

  const reloadParticipants = async () => {
    participantPageCursorsRef.current = { 1: null };
    setParticipantsPage(1);
    await loadParticipatingCompaniesPage({
      page: 1,
      limit: participantColumns * PARTICIPANT_ROWS_PER_PAGE,
      search: debouncedSearchQuery,
    });
  };

  const handleViewRequirements = (participant) => {
    setSelectedParticipant(participant);
    setDisplayModalOpen(true);
  };

  const handleCancelAppointment = async (participant) => {
    if (
      !participant?.idEvento ||
      !participant?.idEmpresaCompradora ||
      !currentUser?.idEmpresa
    ) {
      logger.error("Cannot cancel appointment: required data is missing");
      return;
    }

    try {
      const result = await cancelAppointment(
        participant.idEvento,
        currentUser.idEmpresa,
        participant.idEmpresaCompradora,
        navigate,
      );
      if (result) {
        await reloadParticipants();
      } else {
        logger.error("Failed to delete appointment");
      }
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const getEventCost = () => {
    if (!eventData) {
      return 0;
    }
    if (currentUser?.tipoDeUsuario === "4") {
      return Number(eventData.costoNoSocios) || 0;
    } else if (["1", "2", "3", "5"].includes(currentUser?.tipoDeUsuario)) {
      return Number(eventData.costoSocios) || 0;
    }
    return 0;
  };

  const handleRegister = async (participant) => {
    const currentEventCost = getEventCost();
    if (currentEventCost > 0) {
      setSelectedParticipant(participant);
      setPaymentModalOpen(true);
    } else {
      await handleAppointmentUpdate(participant);
    }
  };

  const handleViewRegistration = async (participant) => {
    if (!participant?.idEvento || !participant?.idEmpresaCompradora) {
      logger.error("Cannot load registration: required data is missing");
      return;
    }
    const company = await getParticipatingCompany(
      participant.idEvento,
      participant.idEmpresaCompradora,
      navigate,
    );
    setSelectedParticipant({
      ...participant,
      ...(company || {}),
      citasAgendadas: participant.citasAgendadas,
      nombreComercial: participant.nombreComercial,
    });
    setViewRegistrationModalOpen(true);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setParticipantsPage(1);
  };

  const handleBack = () => {
    navigate("/events");
  };

  const participantsPerPage = participantColumns * PARTICIPANT_ROWS_PER_PAGE;
  const participantsTotalPages = Math.max(
    1,
    Math.ceil(participantsTotalCount / participantsPerPage) || 1,
  );
  const currentParticipantsPage = Math.min(
    participantsPage,
    participantsTotalPages,
  );

  const handleParticipantsPageChange = (direction) => {
    setParticipantsPage((current) => {
      if (direction === "next") {
        return Math.min(current + 1, participantsTotalPages);
      }
      return Math.max(current - 1, 1);
    });
  };

  const handleAppointmentUpdate = async (participant) => {
    const currentAppointment = participant?.citasAgendadas?.[0];
    if (!currentAppointment?._id || !participant?.idEvento) {
      logger.error("Failed to get current appointment");
      return;
    }

    try {
      const appointmentData = {
        pagado: true,
        pagadoPor: currentUser?._id,
      };
      logger.info("Updating appointment");
      const updatedAppointment = await updateAppointment(
        navigate,
        currentAppointment._id,
        participant.idEvento,
        { appointmentInformation: appointmentData },
      );
      logger.info("updatedAppointment: ", { data: updatedAppointment });
      if (updatedAppointment) {
        await reloadParticipants();
      } else {
        logger.error("Failed to update appointment");
      }
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Back button and title */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <button
              className={styles.returnButtonContainer}
              onClick={handleBack}
            >
              <ChevronLeftIcon className={styles.returnButtonIcon} />
              <span className={styles.returnButtonText}>
                {t("common.goBack")}
              </span>
            </button>
          </div>
          <div className={styles.headerInfo}>
            <h1 className={styles.title}>{eventData?.nombre || ""}</h1>
          </div>
          <div className={styles.searchSection}>
            <SearchBar
              placeholder={
                isSmallMobile || isMediumMobile
                  ? t("pages.businessEventDetails.mobileSearchText")
                  : t("pages.businessEventDetails.searchText")
              }
              value={searchQuery}
              onChange={handleSearch}
              width={getSearchBarWidth(getDevice())}
            />
          </div>
        </div>
      </div>

      <div className={styles.participantsListContainer}>
        <span className={styles.participantsListTitle}>
          {t("pages.businessEventDetails.participants")}
        </span>
        <div
          ref={participantsListRef}
          className={styles.participantsList}
          style={{ "--participant-columns": participantColumns }}
        >
          {participantsLoading && participatingCompanies.length === 0 && (
            <div className={styles.noResults}>
              <CircularProgress size={28} sx={{ color: "#4eabe7" }} />
            </div>
          )}
          {!participantsLoading && participatingCompanies.length === 0 && (
            <div className={styles.noResults}>
              <p>{t("pages.businessEventDetails.noResults")}</p>
            </div>
          )}
          {participatingCompanies.map((participant) => (
            <div
              key={participant.idEmpresaCompradora}
              className={styles.participantCard}
            >
              <div className={styles.participantDetails}>
                <span className={styles.participantName}>
                  {participant.nombreComercial}
                </span>
                <span className={styles.detailLabel}>
                  <strong>
                    {participant.cantidadEjecutivosCompradores ??
                      participant.ejecutivosCompradores?.length ??
                      0}{" "}
                  </strong>
                  {t("pages.businessEventDetails.executives")}
                </span>
                <span className={styles.detailLabel}>
                  <strong>{participant.cantidadRequerimientos ?? 0} </strong>
                  {t("pages.businessEventDetails.requirements")}
                </span>
              </div>
              <div className={styles.buttonsContainer}>
                {participant.rechazado ? (
                  <span className={styles.rejectionMessage}>
                    {t("pages.businessEventDetails.appointmentRejected")}
                  </span>
                ) : participant.registrado ? (
                  <>
                    {participant.citaAgendada ? (
                      <>
                        {participant.citaPagada ? (
                          <button
                            type="button"
                            className={styles.viewRegistrationButton}
                            onClick={() => handleViewRegistration(participant)}
                          >
                            {t("pages.businessEventDetails.viewRegistration")}
                          </button>
                        ) : getEventCost() > 0 ? (
                          <button
                            type="button"
                            className={styles.makePaymentButton}
                            onClick={() => handleRegister(participant)}
                          >
                            {t("pages.businessEventDetails.makePayment")}
                          </button>
                        ) : (
                          <span className={styles.viewAppointmentText}>
                            {t(
                              "pages.businessEventDetails.appointmentRequested",
                            )}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.cancelAppointmentButton}
                          onClick={() => handleCancelAppointment(participant)}
                        >
                          {t("pages.businessEventDetails.cancelAppointment")}
                        </button>
                        <span className={styles.viewAppointmentText}>
                          {t("pages.businessEventDetails.appointmentRequested")}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className={styles.addRequirementsButton}
                    onClick={() => handleViewRequirements(participant)}
                  >
                    {t("pages.businessEventDetails.viewRequirements")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {participantsTotalCount > 0 && (
          <div className={styles.participantsFooter}>
            <PageSelector
              currentPage={currentParticipantsPage}
              totalPages={participantsTotalPages}
              onPageChange={handleParticipantsPageChange}
              disabled={{
                prev: participantsLoading,
                next: participantsLoading,
              }}
            />
          </div>
        )}
      </div>

      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t("pages.businessEvents.creationMessage")}
        </div>
      )}

      {viewRegistrationModalOpen && (
        <ViewBusinessEventRegistrationModal
          open={viewRegistrationModalOpen}
          onClose={() => {
            setSelectedParticipant(null);
            setViewRegistrationModalOpen(false);
          }}
          event={eventData}
          participatingCompany={selectedParticipant}
        />
      )}

      {paymentModalOpen && (
        <AppointmentPaymentModal
          open={paymentModalOpen}
          onClose={() => {
            setSelectedParticipant(null);
            setPaymentModalOpen(false);
            reloadParticipants();
          }}
          event={eventData}
          participatingCompany={selectedParticipant}
          user={currentUser}
        />
      )}

      <BusinessEventRequirementsDisplayModal
        open={displayModalOpen}
        participant={selectedParticipant}
        eventData={eventData}
        currentUser={currentUser}
        autoSelectRequirementId={autoSelectRequirementId}
        onClose={(participant = null) => {
          setSelectedParticipant(null);
          setDisplayModalOpen(false);
          setAutoSelectRequirementId(null);
          if (participant) {
            reloadParticipants();
          }
        }}
      />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default BusinessEventDetails;
